import React from 'react';
import type { FC } from 'react';
import { Box, Text } from '@wix/cli-ui-kit';
import type {
  HttpApplicationErrorResponse,
  HttpValidationErrorResponse,
} from '@wix/cli-http-client';
import {
  isHttpApplicationError,
  isHttpValidationError,
  extractHttpError,
} from '@wix/cli-http-client';
import type { IHttpError } from '@wix/http-client';

const Rows: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box flexDirection="column" paddingTop={1} paddingBottom={1}>
      {children}
    </Box>
  );
};

const Row: FC<{ label: string; value?: string }> = ({ label, value }) => {
  return value ? (
    <Text>
      {label}: <Text skin="warning">{value}</Text>
    </Text>
  ) : null;
};

const HttpApplicationErrorDetails: FC<{
  httpError: IHttpError<HttpApplicationErrorResponse>;
}> = ({ httpError }) => {
  const data = httpError.response?.data;
  const applicationError = data?.details.applicationError;

  return (
    <Rows>
      <Text>Extended error output:</Text>
      <Row label="Request ID" value={httpError.requestId} />
      <Row label="Code" value={applicationError?.code} />
      <Row label="Message" value={data?.message} />
      <Row label="Description" value={applicationError?.description} />
    </Rows>
  );
};

const HttpValidationErrorDetails: FC<{
  httpError: IHttpError<HttpValidationErrorResponse>;
}> = ({ httpError }) => {
  const data = httpError.response?.data;
  const fieldViolations = data?.details.validationError.fieldViolations;

  const violations = fieldViolations
    ?.map((violation) => `${violation.field}: ${violation.description}`)
    .join('\n');

  return (
    <Rows>
      <Text>Extended error output:</Text>
      <Row label="Request ID" value={httpError.requestId} />
      <Row label="Message" value={data?.message} />
      <Row label="Violations" value={violations} />
    </Rows>
  );
};

export const HttpErrorDetails: FC<{ error?: unknown }> = ({ error }) => {
  const httpError = extractHttpError(error);

  if (httpError) {
    try {
      if (isHttpApplicationError(httpError)) {
        return <HttpApplicationErrorDetails httpError={httpError} />;
      }

      if (isHttpValidationError(httpError)) {
        return <HttpValidationErrorDetails httpError={httpError} />;
      }
    } catch {
      return null;
    }
  }

  return null;
};
